import React, { useState, useContext, useEffect } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Button, Typography, Autocomplete, TextField, MenuItem, FormControl, Select, InputLabel } from '@mui/material';
import Chip from '@mui/material/Chip';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { AuthContext } from '../AuthContextProvider';
import ErrorDialog from '../ErrorDialog';
import { useNavigate, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { rpc } from '../utils/RpcUtils';
import { isValidDateTime } from '../utils/DateUtils';
import * as Constants from '../Constants';
import '../App.css';

const ClassDetails = () => {
  const [owner, setOwner] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [repeat, setRepeat] = useState('0');
  const [title, setTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const userTimeZone = user._site.timezone;
  const [selectedDate, setSelectedDate] = useState(DateTime.now().setZone(userTimeZone));
  let { id } = useParams();

  useEffect(() => {
    const onFetchSuccess = (scheduledEvent) => {
      setOwner(scheduledEvent._owner);
      setTitle(scheduledEvent.title);
      setSelectedEvent(scheduledEvent._event);
      setSelectedDate(DateTime.fromISO(scheduledEvent.shownOn, { zone: userTimeZone }));
      setSelectedGroups(scheduledEvent._eventGroups);
    };

    const fetchScheduledEvent = async (id) => {
      rpc({
        method: 'GetScheduledEvent',
        params: { _id: id },
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: onFetchSuccess
      });
    };

    const getEvents = async () => {
      rpc({
        method: 'GetEvents',
        params: { _site: user._site._id },
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setEvents
      });
    };

    const getGroups = async () => {
      rpc({
        method: 'GetEventGroups',
        params: {},
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setGroups
      });
    };

    if (id) {
      fetchScheduledEvent(id);
    }
    getEvents();
    getGroups();
  }, [id, user, userTimeZone]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleEventChange = (event, value) => {
    setSelectedEvent(value);
  };

  const onSuccess = () => {
    navigate('/classes');
  };

  const createScheduledEvent = async () => {
    if (!title?.trim() || !isValidDateTime(selectedDate) || selectedGroups.length === 0 || !selectedEvent) {
      setErrorMessage('Title, Date, Event and Group fields are required');
      setOpenErrorDialog(true);
      return;
    }
    const eventGroups = selectedGroups.map((group) => group._id);
    const params = {
      shownOn: selectedDate.toISO(),
      _event: selectedEvent,
      _eventGroups: eventGroups,
      _owner: user._site._id,
      title: title,
      repeat
    };
    rpc({
      method: 'CreateScheduledEvent',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const updateScheduledEvent = async () => {
    if (!title?.trim() || !isValidDateTime(selectedDate) || selectedGroups.length === 0 || !selectedEvent) {
      setErrorMessage('Title, Date, Event and Group fields are required');
      setOpenErrorDialog(true);
      return;
    }
    const eventGroups = selectedGroups.map((group) => group._id);
    const params = {
      _id: id,
      shownOn: selectedDate.toISO(),
      _event: selectedEvent,
      _eventGroups: eventGroups,
      title: title,
    };
    rpc({
      method: 'UpdateScheduledEvent',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const deleteScheduledEvent = async () => {
    rpc({
      method: 'DeleteScheduledEvent',
      params: { _id: id },
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const handleClose = () => setOpenErrorDialog(false);
  const handleRepeatChange = (event) => setRepeat(event.target.value);

  const getButtons = () => {
    const isEditable = owner === null || owner === user._site._id;
    if (id) {
      return (
        <div className="HorizontalContainer">
          <Button className="Button" variant="contained" color="primary" onClick={updateScheduledEvent}>
            Update
          </Button>
          {isEditable && (
            <Button className="Button" variant="outlined" color="error" onClick={deleteScheduledEvent}>
              Delete
            </Button>
          )}
        </div>
      );
    }
    return (
      <>
        <FormControl className="Select" sx={{ marginTop: "1em", width: 600 }}>
          <InputLabel id="repeat-label">Repeat</InputLabel>
          <Select
            labelId="repeat-label"
            id="repeat"
            value={repeat}
            label="Repeat"
            onChange={handleRepeatChange}
          >
            <MenuItem value="0">Do not repeat</MenuItem>
            <MenuItem value="1">1 week</MenuItem>
            <MenuItem value="2">2 weeks</MenuItem>
            <MenuItem value="3">3 weeks (one month)</MenuItem>
          </Select>
        </FormControl>
        <Button className="Button" variant="contained" color="primary" onClick={createScheduledEvent}>
          Create
        </Button>
      </>
    );
  };

  const handleGroupChange = (event, newValue, reason) => {
    if (reason === 'removeOption' || reason === 'popperClose') {
      const removed = selectedGroups.filter((group) => !newValue.find((g) => g._id === group._id));
      const disallowedRemovals = removed.filter(
        (group) => group._site?._id?.toString() !== user._site._id?.toString()
      );
      setSelectedGroups([...newValue, ...disallowedRemovals]);
    } else {
      setSelectedGroups(newValue);
    }
  };

  const getEventLabel = (event) => {
    let label = event.displayName;
    event.attributes.forEach((attribute) => {
      if (attribute.name === Constants.ATTR_PLAY_IMMEDIATELY && attribute.value === 'true') {
        label += ' (immediate)';
      }
    });
    return label;
  };

  const isLocked = owner !== null && owner !== user._site._id;

  return (
    <div className="ClassDetails">
      <Typography variant="h6" gutterBottom>
        Scheduled Class {title}
      </Typography>
      <TextField
        className="TextField"
        label="Title"
        value={title}
        disabled={isLocked}
        onChange={(e) => setTitle(e.target.value)}
      />
      
      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en">
        <DateTimePicker
          className="DateTimePicker"
          label="Select Date"
          disabled={isLocked}
          value={selectedDate}
          onChange={handleDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          format={Constants.DATE_TIME_FORMAT_LUXON}
          minDate={DateTime.now()}
        />
      </LocalizationProvider>

      <Autocomplete
        className="Autocomplete"
        value={selectedEvent}
        isOptionEqualToValue={(event, value) => event?._id === value?._id}
        options={events}
        onChange={handleEventChange}
        getOptionLabel={(event) => getEventLabel(event)}
        disabled={isLocked}
        renderInput={(params) => <TextField {...params} label="Event" variant="outlined" />}
      />
      
      <Autocomplete
        className="Autocomplete"
        multiple
        value={selectedGroups}
        isOptionEqualToValue={(group, value) => group?._id === value?._id}
        options={groups.filter((group) => group._site?._id === user._site._id)}
        onChange={handleGroupChange}
        getOptionLabel={(group) => `${group._site?.name ?? 'Unknown'} ${group.displayName}`}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const isGroupLocked = option._site?._id?.toString() !== user._site._id?.toString();
            return (
              <Chip
                key={option._id}
                label={`${option._site?.name ?? 'Unknown'} ${option.displayName}`}
                {...getTagProps({ index })}
                onDelete={isGroupLocked ? undefined : getTagProps({ index }).onDelete}
                style={isGroupLocked ? { opacity: 0.6, pointerEvents: 'none' } : {}}
              />
            );
          })
        }
        renderInput={(params) => <TextField {...params} label="Groups" variant="outlined" />}
      />
      {getButtons()}
      <ErrorDialog open={openErrorDialog} onErrorDialogClose={handleClose} errorMessage={errorMessage} />
    </div>
  );
};

export default ClassDetails;
